/**
 * Supplier Detail Page
 *
 * Refactored to use modular sub-components for improved maintainability.
 * All state management is handled by the useSupplierDetail hook; the header
 * and KPI cards live in ./supplier-detail/SupplierHeader and SupplierKpiCards.
 */

import { useNavigate } from 'react-router-dom';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Loader2, ArrowLeft } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useFormatters } from '../contexts/PreferencesContext';
import { isCompanyAdminUser } from '../lib/companyEditAccess';
import { useMenuSettings } from '../contexts/MenuSettingsContext';

// Import modular components
import {
  useSupplierDetail,
  OverviewTab,
  PerformanceTab,
  OrdersTab,
  ContractsTab,
  QualityTab,
  UsersTab,
  UserModal,
  EditUserDialog,
  ActivityTab,
  CreditScoreTab,
  QuestionnairesTab,
  SettingsTab,
  DocumentsTab,
  ApprovalsTab,
  CalendarTab,
  SupplierHeader,
  SupplierKpiCards,
} from './supplier-detail';
import GroupStructureTab from './supplier-detail/GroupStructureTab';
import AuditsTab from './supplier-detail/AuditsTab';
import { canShowAuditsTab, useAuditSummary } from './supplier-detail/useAuditSummary';

export default function SupplierDetail() {
  const navigate = useNavigate();
  const { formatCurrency: formatCurrencyFromHook } = useFormatters();
  const { supplierProfileLayout, auditLiteEnabled } = useMenuSettings();

  const {
    // State
    supplier,
    companyInfo,
    orders,
    ordersPage,
    ordersCount,
    ordersPageSize,
    ordersSupplierCode,
    fetchOrders,
    setOrdersSupplierCode,
    ncrs,
    users,
    loading,
    ordersLoading,
    ncrsLoading,
    usersLoading,
    error,
    activeTab,
    selectedUser,
    userModalOpen,
    userModalTab,
    userSitePermissions,
    permissionsLoading,
    savingPermission,
    impersonating,
    // CR-5857
    editUser,
    editUserOpen,

    // Actions
    setActiveTab,
    handleOpenUserModal,
    handleCloseUserModal,
    handleToggleSiteLink,
    handleUpdatePermission,
    setUserSearch,
    handleUpdateProfile,
    handleUpdateCompany,
    handleUploadLogo,
    handleImpersonate,
    setUserModalTab,
    fetchSupplierData,
    fetchUsers,
    // CR-5857
    handleOpenEditUser,
    handleCloseEditUser,
    handleEditUserSaved,
    handleResetPassword,

    // Computed
    filteredUsers,
    isHelixAdmin,
    isCustomer,
    isCustomerAdmin,
    isOwnCompany,
    userSearch,
    usersAccessCode,
  } = useSupplierDetail();

  const { user } = useAuth();
  // Mirrors CompanyViewSet.upload_logo: platform users, or an admin editing
  // their own company or (customer side) a supplier they manage. A non-admin
  // would only be offered an upload the server refuses.
  const canEditLogo = isHelixAdmin || (isCompanyAdminUser(user) && (isCustomer || isOwnCompany));

  // Audit Lite: customer side + Helix (read only), never the supplier's own view.
  // `isCustomer` is true for Helix staff too; only customer users may act.
  const showAudits = canShowAuditsTab({
    auditLiteEnabled,
    isCustomer,
    isOwnCompany,
    layoutAllows: supplierProfileLayout.tabs['audits'] ?? true,
  });
  const auditSummary = useAuditSummary(supplier?.company ?? 0, showAudits);
  const openAuditActions = auditSummary.summary?.open_actions ?? 0;
  const overdueAuditActions = auditSummary.summary?.overdue_actions ?? 0;

  // Format currency helper
  const formatCurrency = (value: string | number) => {
    const num = typeof value === 'string' ? parseFloat(value) : value;
    return formatCurrencyFromHook(num);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  if (error || !supplier) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">Error: {error || 'Supplier not found'}</p>
          <Button onClick={() => navigate(-1)} variant="outline" className="mt-4">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header: logo, name, badges, active toggle, Contact */}
      <SupplierHeader
        supplier={supplier}
        companyInfo={companyInfo}
        canEditLogo={canEditLogo}
        isCustomerAdmin={isCustomerAdmin}
        onUploadLogo={handleUploadLogo}
        onChanged={fetchSupplierData}
      />

      {/* Key Metrics Cards */}
      {/* CR-5767: Replaced OTD KPI with Performance Rating */}
      <SupplierKpiCards
        supplier={supplier}
        showRating={supplierProfileLayout.stats.otd}
        showTotalOrders={supplierProfileLayout.stats.total_orders}
      />

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className="w-full justify-start overflow-x-auto flex-nowrap scrollbar-hide [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
          <TabsTrigger value="overview">Company Overview</TabsTrigger>
          {supplierProfileLayout.tabs.performance && <TabsTrigger value="performance">Performance</TabsTrigger>}
          {supplierProfileLayout.tabs.orders && <TabsTrigger value="orders">Orders</TabsTrigger>}
          {supplierProfileLayout.tabs.contracts && <TabsTrigger value="contracts">Contracts</TabsTrigger>}
          {supplierProfileLayout.tabs.quality && <TabsTrigger value="quality">Quality</TabsTrigger>}
          {supplierProfileLayout.tabs.questionnaires && <TabsTrigger value="questionnaires">Questionnaires</TabsTrigger>}
          {showAudits && (
            <TabsTrigger value="audits">
              Audits
              {/* Open actions (overdue included); red while any is overdue. */}
              {openAuditActions > 0 && (
                <Badge variant={overdueAuditActions > 0 ? 'destructive' : 'secondary'} className="ml-1.5"
                  title={overdueAuditActions > 0
                    ? `${openAuditActions} open audit actions, ${overdueAuditActions} overdue`
                    : `${openAuditActions} open audit actions`}>
                  {openAuditActions}
                </Badge>
              )}
            </TabsTrigger>
          )}
          {supplierProfileLayout.tabs.credit_scores && <TabsTrigger value="credit-scores">Credit Scores</TabsTrigger>}
          {supplierProfileLayout.tabs.users && <TabsTrigger value="users">Users</TabsTrigger>}
          {/* Defaults to visible when an existing tenant's layout JSON
              predates the documents key. Bracket access because documents
              isn't an explicit field on SupplierProfileLayout.tabs. */}
          {(supplierProfileLayout.tabs['documents'] ?? true) && <TabsTrigger value="documents">Documents</TabsTrigger>}
          {/* Approvals tab - displays supplier certifications/accreditations */}
          {(supplierProfileLayout.tabs['approvals'] ?? true) && <TabsTrigger value="approvals">Approvals</TabsTrigger>}
          {/* Calendar tab - displays supplier-specific calendar events */}
          {(supplierProfileLayout.tabs['calendar'] ?? true) && <TabsTrigger value="calendar">Calendar</TabsTrigger>}
          {/* CR-4773: Renamed to match the card content ("Communication Audit Log") */}
          {supplierProfileLayout.tabs.activity_log && <TabsTrigger value="activity">Communication Log</TabsTrigger>}
          <TabsTrigger value="settings">Settings</TabsTrigger>
          {supplierProfileLayout.tabs.group_structure && <TabsTrigger value="group-structure">Group Structure</TabsTrigger>}
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-4">
          <OverviewTab
            supplier={supplier}
            companyInfo={companyInfo}
            canEdit={isHelixAdmin || isCustomer}
            onUpdateProfile={handleUpdateProfile}
            onUpdateCompany={handleUpdateCompany}
          />
        </TabsContent>

        {/* Performance Tab */}
        <TabsContent value="performance" className="space-y-4">
          <PerformanceTab supplier={supplier} />
        </TabsContent>

        {/* Orders Tab */}
        <TabsContent value="orders" className="space-y-4">
          <OrdersTab
            supplier={supplier}
            orders={orders}
            ordersLoading={ordersLoading}
            formatCurrency={formatCurrency}
            ordersPage={ordersPage}
            ordersCount={ordersCount}
            ordersPageSize={ordersPageSize}
            ordersSupplierCode={ordersSupplierCode}
            fetchOrders={fetchOrders}
            setOrdersSupplierCode={setOrdersSupplierCode}
          />
        </TabsContent>

        {/* Contracts Tab */}
        <TabsContent value="contracts" className="space-y-4">
          <ContractsTab supplier={supplier} />
        </TabsContent>

        {/* Quality Tab */}
        <TabsContent value="quality" className="space-y-4">
          <QualityTab supplier={supplier} ncrs={ncrs} ncrsLoading={ncrsLoading} />
        </TabsContent>

        {/* Questionnaires Tab */}
        <TabsContent value="questionnaires" className="space-y-4">
          <QuestionnairesTab supplierId={supplier.company} supplierName={supplier.company_name} />
        </TabsContent>

        {/* Audits Tab (Audit Lite) */}
        {showAudits && (
          <TabsContent value="audits" className="space-y-4">
            <AuditsTab supplierId={supplier.company} supplierName={supplier.company_name}
              canEdit={isCustomer && !isHelixAdmin} summaryState={auditSummary} />
          </TabsContent>
        )}

        {/* Credit Scores Tab */}
        <TabsContent value="credit-scores" className="space-y-4">
          <CreditScoreTab supplier={supplier} />
        </TabsContent>

        {/* Users Tab */}
        <TabsContent value="users" className="space-y-4">
          <UsersTab
            users={users}
            filteredUsers={filteredUsers}
            usersLoading={usersLoading}
            userSearch={userSearch}
            impersonating={impersonating}
            isHelixAdmin={isHelixAdmin}
            isCustomer={isCustomer}
            isCustomerAdmin={isCustomerAdmin}
            companyId={supplier?.company || 0}
            companyName={supplier?.company_name || ''}
            groupId={supplier?.company_details?.group}
            onUserSearchChange={setUserSearch}
            onOpenUserModal={handleOpenUserModal}
            onImpersonate={handleImpersonate}
            onRefreshUsers={fetchUsers}
            onEditUser={handleOpenEditUser}
            onResetPassword={handleResetPassword}
            usersAccessCode={usersAccessCode}
            companyType={supplier?.company_details?.company_type}
          />
        </TabsContent>

        {/* Documents Tab */}
        <TabsContent value="documents" className="space-y-4">
          <DocumentsTab supplier={supplier} />
        </TabsContent>

        {/* Approvals Tab - Supplier certifications and accreditations */}
        <TabsContent value="approvals" className="space-y-4">
          <ApprovalsTab
            companyId={supplier.company}
            approval={{
              approval_level: supplier.approval_level ?? null,
              approval_level_display: supplier.approval_level_display ?? null,
              approved_at: supplier.approved_at ?? null,
              approved_by_name: supplier.approved_by_name ?? null,
            }}
          />
        </TabsContent>

        {/* Calendar Tab - Supplier-specific calendar events */}
        <TabsContent value="calendar" className="space-y-4">
          <CalendarTab supplier={supplier} onTabChange={setActiveTab} />
        </TabsContent>

        {/* CR-4477: Activity / Traceability Log Tab */}
        <TabsContent value="activity" className="space-y-4">
          <ActivityTab supplier={supplier} />
        </TabsContent>

        {/* Settings Tab */}
        <TabsContent value="settings" className="space-y-4">
          <SettingsTab
            supplier={supplier}
            isHelixAdmin={isHelixAdmin}
            isCustomer={isCustomer}
            isCustomerAdmin={isCustomerAdmin}
            onUpdateProfile={handleUpdateProfile}
          />
        </TabsContent>

        {/* Group Structure Tab */}
        <TabsContent value="group-structure" className="space-y-4">
          <GroupStructureTab
            supplier={supplier}
            isHelixAdmin={isHelixAdmin}
            isCustomer={isCustomer}
          />
        </TabsContent>
      </Tabs>

      {/* User Detail Modal */}
      <UserModal
        open={userModalOpen}
        onOpenChange={handleCloseUserModal}
        selectedUser={selectedUser}
        userModalTab={userModalTab}
        onTabChange={setUserModalTab}
        userSitePermissions={userSitePermissions}
        permissionsLoading={permissionsLoading}
        savingPermission={savingPermission}
        impersonating={impersonating}
        isHelixAdmin={isHelixAdmin}
        isCustomer={isCustomer}
        isCustomerAdmin={isCustomerAdmin}
        contextCompanyName={supplier?.company_name}
        onToggleSiteLink={handleToggleSiteLink}
        onUpdatePermission={handleUpdatePermission}
        onImpersonate={handleImpersonate}
        onEditUser={handleOpenEditUser}
        onResetPassword={handleResetPassword}
      />

      {/* CR-5857: Edit Profile dialog */}
      <EditUserDialog
        open={editUserOpen}
        onOpenChange={(open) => { if (!open) handleCloseEditUser(); }}
        userProfile={editUser}
        onSaved={handleEditUserSaved}
        companyId={supplier?.company || 0}
        companyName={supplier?.company_name || ''}
      />
    </div>
  );
}
